import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import { BusinessDetails } from '@App/common/models';
import { PageTitleService } from '@App/common/services/title.service';
import { environment } from '@Environments';
import {
  CardSocialImage,
  getCardCanonicalUrl,
  getCardSocialDescription,
  getCardSocialImage,
  getCardSocialTitle
} from './pitch-card-route.utils';

export interface CardSocialMetadata {
  canonicalUrl: string;
  description: string;
  image: CardSocialImage;
  title: string;
}

export function buildCardSocialMetadata(
  card: BusinessDetails,
  routeAlias: string
): CardSocialMetadata {
  return {
    canonicalUrl: getCardCanonicalUrl(routeAlias),
    description: getCardSocialDescription(card),
    image: getCardSocialImage(card),
    title: getCardSocialTitle(card)
  };
}

@Injectable({ providedIn: 'root' })
export class PitchCardSocialMetaService {
  private readonly document = inject(DOCUMENT);
  private readonly meta = inject(Meta);
  private readonly titleService = inject(PageTitleService);

  apply(card: BusinessDetails, routeAlias: string): void {
    this.clear();

    const config = buildCardSocialMetadata(card, routeAlias);
    this.titleService.setTitle(config.title);
    this.upsert({ name: 'description', content: config.description });
    this.upsert({
      name: 'robots',
      content: environment.production ? 'index, follow' : 'noindex, nofollow'
    });

    const openGraphTags: MetaDefinition[] = [
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: config.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: config.canonicalUrl },
      { property: 'og:site_name', content: 'Pitch59' },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:image', content: config.image.url },
      { property: 'og:image:secure_url', content: config.image.url },
      { property: 'og:image:type', content: config.image.type },
      { property: 'og:image:width', content: String(config.image.width) },
      { property: 'og:image:height', content: String(config.image.height) },
      {
        property: 'og:image:alt',
        content: `${config.title} PitchCard preview`
      }
    ];

    const twitterTags: MetaDefinition[] = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: config.title },
      { name: 'twitter:description', content: config.description },
      { name: 'twitter:image', content: config.image.url },
      {
        name: 'twitter:image:alt',
        content: `${config.title} PitchCard preview`
      }
    ];

    [...openGraphTags, ...twitterTags].forEach((tag) => this.upsert(tag));
    this.setCanonical(config.canonicalUrl);
  }

  applyNotFound(): void {
    this.clear();
    this.titleService.setTitle('PitchCard not found | Pitch59');
    this.upsert({
      name: 'description',
      content: 'This PitchCard is not available.'
    });
    this.upsert({ name: 'robots', content: 'noindex, nofollow' });
  }

  clear(): void {
    [
      'name="description"',
      'property="og:type"',
      'property="og:title"',
      'property="og:description"',
      'property="og:url"',
      'property="og:site_name"',
      'property="og:locale"',
      'property="og:image"',
      'property="og:image:secure_url"',
      'property="og:image:type"',
      'property="og:image:width"',
      'property="og:image:height"',
      'property="og:image:alt"',
      'name="twitter:card"',
      'name="twitter:title"',
      'name="twitter:description"',
      'name="twitter:image"',
      'name="twitter:image:alt"'
    ].forEach((selector) => {
      while (this.meta.getTag(selector)) {
        this.meta.removeTag(selector);
      }
    });

    this.document
      .querySelectorAll<HTMLLinkElement>('link[rel="canonical"]')
      .forEach((link) => link.remove());
  }

  private setCanonical(url: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
  }

  private upsert(tag: MetaDefinition): void {
    const selector = tag.name
      ? `name="${tag.name}"`
      : `property="${tag.property}"`;
    this.meta.updateTag(tag, selector);
  }
}
